import { CatalystInsightType, DetailedCatalystInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { InsightTypePill } from '~/components/insight-type-pill/InsightTypePill';
import { InsightsLivePrice } from '~/components/insights-live-price/InsightsLivePrice';
import { ThemeHeaderDetailed } from '~/components/theme-header-detailed/ThemeHeaderDetailed';
import { useEntity } from '~/hooks/use-entities';
import { useTheme } from '~/hooks/use-theme-overview/useThemeOverview';
import { CompanyMarketReactionChart } from '~/modules/market-reaction-charts/company-market-reaction-chart/CompanyMarketReactionChart';
import { EntityHeaderWatchlist } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';

import { CatalystThemeMarketReaction } from '../catalyst-theme-market-reaction/CatalystThemeMarketReaction';
import * as S from './CatalystDrawerHeader.styles';

export interface CatalystDrawerHeaderProps {
  identifier: string;
  catalystInsight: DetailedCatalystInsight;
  type: CatalystInsightType;
}

export const CatalystDrawerHeader = ({
  identifier,
  catalystInsight,
  type,
}: CatalystDrawerHeaderProps) => {
  const { t } = useTranslation('insights');
  const entityTag = type === 'company' ? identifier : undefined;
  const themeId = type === 'theme' ? identifier : undefined;
  const { data: entityData } = useEntity(entityTag);

  const { data: themeData } = useTheme({
    themeId,
  });

  const entity = entityData?.data;

  /* i18next-extract-mark-context-next-line ["financial_operational_updates", "corporate_actions_strategic_announcements", "management_leadership_changes", "legal_regulatory_events"] */
  const translatedSubCategory = t(
    `catalyst.subcategories.${catalystInsight.sub_category}`
  );

  return (
    <S.Root>
      <InsightTypePill createdAt={catalystInsight.created_at} category={type} />
      <div>
        <S.SubCategoryActionTypeWrapper>
          {`${translatedSubCategory} · ${catalystInsight.action_type}`}
        </S.SubCategoryActionTypeWrapper>
        <S.Title>{catalystInsight.title}</S.Title>
      </div>
      <S.FlexWrapper>
        {entity && (
          <>
            <EntityHeaderWatchlist entity={entity} />
            <InsightsLivePrice entity={entity} showPricePercentage />
          </>
        )}
        {themeData && <ThemeHeaderDetailed theme={themeData} />}
      </S.FlexWrapper>
      <ErrorBoundaryWrapper>
        {type === 'company' ? (
          <CompanyMarketReactionChart
            reportingDate={catalystInsight.created_at}
            subscribableTicker={entity?.subscribable_ticker ?? ''}
          />
        ) : (
          <CatalystThemeMarketReaction themeId={themeId} />
        )}
      </ErrorBoundaryWrapper>
    </S.Root>
  );
};
